
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import {COL, sqlSum, sqlSumByDocType, getTableName} from '@/worker/db/sqlHelpers';
import FilterWidget from '../shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
    components: {
        AmountDisplayerFilterable,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class SalesNumberDisplayer extends FilterWidget {
    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'docproject'
        | 'dtlproject'
    > = [];

    public salesCount = 0;
    public salesAmount = '' || 0;
    public activeCustomersCount = 0;
    public invoiceCount = 0;

    public get permissionIds(): PermissionsGroup[] {
        return ['sales', 'customers'];
    }

    public get dateFormatted() {
        return this.selectedDateRange;
    }

    public get currentYear() {
        return moment(this.selectedAsOfDate).year();
    }

    public get currency() {
        return gdbx.currencySymbol;
    }

    public get decimal() {
        return gdbx.numDecimal;
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        // Build filter clauses manually for cross-table UNION
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;

        let docProjectFilter = '';
        if (this.selectedDocProjects.length > 0) {
            const list = this.selectedDocProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            docProjectFilter = ` AND "DOCPROJECT" IN (${list})`;
        }

        let dtlProjectFilter = '';
        if (this.selectedDTLProjects.length > 0) {
            const list = this.selectedDTLProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            dtlProjectFilter = ` AND "DTLPROJECT" IN (${list})`;
        }

        const baseFilter = `${dateFilter}${docProjectFilter}${dtlProjectFilter}`;

        // Get table names
        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const arTable = getTableName('customerTrades');

        // Get a head for executing queries (no filters needed)
        const queryHead = await this._ensureTablesLoaded([
            'sales', 'creditNotes', 'customerTrades',
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.9);

        // Run SL and AR queries in parallel
        const [slResult, arResult, arRefsResult] = await Promise.all([
            // Single UNION query for SL data (sales + creditNotes)
            queryHead.rawQuery(`
                SELECT
                    SUM(CASE WHEN src = 'sales' AND docType IN ('IV', 'CS') THEN amount ELSE 0 END) as iv_cs_amount,
                    SUM(CASE WHEN src = 'sales' AND docType = 'DN' THEN amount ELSE 0 END) as dn_amount,
                    SUM(CASE WHEN src = 'cn' THEN amount ELSE 0 END) as cn_amount,
                    COUNT(DISTINCT CASE WHEN src = 'sales' AND docType IN ('IV', 'CS') THEN docNo END) as iv_cs_docs,
                    COUNT(DISTINCT CASE WHEN src = 'sales' AND docType = 'DN' THEN docNo END) as dn_docs,
                    COUNT(DISTINCT CASE WHEN src = 'cn' THEN docNo END) as cn_docs,
                    COUNT(DISTINCT CASE WHEN src = 'sales' THEN code END) as sl_customers,
                    COUNT(DISTINCT CASE WHEN src = 'cn' THEN code END) as cn_customers
                FROM (
                    SELECT 'sales' as src, ${COL.DOCTYPE} as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
                        ${COL.DOCNO} as docNo, ${COL.CODE} as code
                    FROM ${salesTable}
                    WHERE ${baseFilter}
                    UNION ALL
                    SELECT 'cn' as src, 'CN' as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
                        ${COL.DOCNO} as docNo, ${COL.CODE} as code
                    FROM ${cnTable}
                    WHERE ${baseFilter}
                ) AS combined
            `),
            // Single query for AR data
            queryHead.rawQuery(`
                SELECT
                    ${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
                    ${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
                    ${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
                    COUNT(DISTINCT ${COL.CODE}) as ar_customers
                FROM ${arTable}
                WHERE ${baseFilter}
            `),
            // AR DocRefs query (expand pipe-delimited DOCREFS for case counting)
            queryHead.rawQuery(`
                SELECT ${COL.DOCTYPE} as docType, COUNT(DISTINCT ref) as refCount
                FROM (
                    SELECT ${COL.DOCTYPE},
                        UNNEST(string_split("DOCREFS", '|')) as ref
                    FROM ${arTable}
                    WHERE ${baseFilter}
                ) sub
                WHERE ref IS NOT NULL AND TRIM(ref) != ''
                GROUP BY ${COL.DOCTYPE}
            `),
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.95);

        // Extract SL values
        const slRow = slResult[0] || {};
        const slIvCsAmount = Number(slRow.iv_cs_amount) || 0;
        const slDnAmount = Number(slRow.dn_amount) || 0;
        const slCnAmount = Number(slRow.cn_amount) || 0;
        const slIvCsDocs = Number(slRow.iv_cs_docs) || 0;
        const slDnDocs = Number(slRow.dn_docs) || 0;
        const slCnDocs = Number(slRow.cn_docs) || 0;
        const slCustomers = Number(slRow.sl_customers) || 0;
        const cnCustomers = Number(slRow.cn_customers) || 0;

        // Extract AR values
        const arRow = arResult[0] || {};
        const arIvAmount = Number(arRow.iv_amount) || 0;
        const arCnAmount = Number(arRow.cn_amount) || 0;
        const arDnAmount = Number(arRow.dn_amount) || 0;
        const arCustomers = Number(arRow.ar_customers) || 0;

        // Extract AR DocRefs counts
        const getRefCount = (docType: string): number => {
            const row = arRefsResult.find((r: any) => String(r.docType) === docType);
            return row ? (Number(row.refCount) || 0) : 0;
        };

        let salesAmount: number;
        let invoiceCount: number;
        let activeCustomersCount: number;

        if (this.slPhModuleOnly) {
            // SL Module calculations
            salesAmount = slIvCsAmount
                - (this.includeCreditNote ? slCnAmount : 0)
                + (this.includeDebitNote ? slDnAmount : 0);

            invoiceCount = slIvCsDocs
                + (this.includeDebitNote ? slDnDocs : 0)
                - (this.includeCreditNote ? slCnDocs : 0);

            // Use combined customer count from UNION (already distinct)
            activeCustomersCount = Math.max(slCustomers, cnCustomers);
        } else {
            // AR Module calculations
            salesAmount = arIvAmount
                - (this.includeCreditNote ? arCnAmount : 0)
                + (this.includeDebitNote ? arDnAmount : 0);

            // AR invoice count from expanded DOCREFS
            invoiceCount = getRefCount('IV')
                + (this.includeDebitNote ? getRefCount('DN') : 0)
                - (this.includeCreditNote ? getRefCount('CN') : 0);

            activeCustomersCount = arCustomers;
        }

        this.invoiceCount = invoiceCount;
        this.activeCustomersCount = activeCustomersCount;
        this.salesAmount = salesAmount;

        this.generateLoadingText(1);
        this.saveHistory('salesAmount', 'invoiceCount', 'activeCustomersCount');
    }
}
